import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import {
  fetchBillingAdminDashboard,
  fetchBillingAdminPurchases,
  formatExamPrice,
} from '../services/api';
import './PaymentDashboardPage.css';

const MEDALS = ['🥇', '🥈', '🥉'];

function fmt(cents, currency = 'eur') {
  return formatExamPrice(cents, currency);
}

function fmtDate(value, locale) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
}

function PeriodCards({ title, data, currency, t }) {
  if (!data) return null;
  const items = [
    { key: 'day', label: t('paymentDashboard.period_day') },
    { key: 'week', label: t('paymentDashboard.period_week') },
    { key: 'month', label: t('paymentDashboard.period_month') },
  ];
  return (
    <div className="payment-card">
      <h2>{title}</h2>
      <div className="payment-dashboard-grid cols-3" style={{ marginTop: 0 }}>
        {items.map(({ key, label }) => (
          <div key={key} className="payment-kpi">
            <span className="label">{label}</span>
            <span className="value">{fmt(data[key]?.ttc, currency)}</span>
            <span className="sub">
              {t('paymentDashboard.sales_count', { count: data[key]?.count || 0 })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrendChart({ rows, valueKey = 'ttc', labelKey = 'date', currency, t }) {
  const max = useMemo(
    () => Math.max(1, ...(rows || []).map((r) => Number(r[valueKey]) || 0)),
    [rows, valueKey]
  );
  if (!rows?.length) {
    return <p style={{ color: '#94a3b8', margin: 0 }}>{t('paymentDashboard.no_data')}</p>;
  }
  return (
    <div className="trend-bars" title={t('paymentDashboard.trend_hint')}>
      {rows.map((row) => {
        const v = Number(row[valueKey]) || 0;
        const pct = Math.max(2, Math.round((v / max) * 100));
        const label = String(row[labelKey] || '').slice(5);
        return (
          <div key={row[labelKey] || row.week || row.date} className="trend-bar-col">
            <div className="trend-bar" style={{ height: `${pct}%` }} title={fmt(v, currency)} />
            <span className="trend-bar-label">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function MonthlyTrendSection({ rows, currency, t }) {
  const chart = useMemo(() => {
    if (!rows?.length) return null;
    const width = 640;
    const height = 180;
    const pad = { top: 16, right: 48, bottom: 28, left: 48 };
    const innerW = width - pad.left - pad.right;
    const innerH = height - pad.top - pad.bottom;
    const maxTtc = Math.max(1, ...rows.map((r) => Number(r.ttc) || 0));
    const maxCount = Math.max(1, ...rows.map((r) => Number(r.count) || 0));
    const xAt = (i) => pad.left + (rows.length <= 1 ? innerW / 2 : (i / (rows.length - 1)) * innerW);
    const yTtc = (v) => pad.top + innerH - (v / maxTtc) * innerH;
    const yCount = (v) => pad.top + innerH - (v / maxCount) * innerH;

    const ttcPoints = rows.map((r, i) => `${xAt(i)},${yTtc(Number(r.ttc) || 0)}`).join(' ');
    const countPoints = rows.map((r, i) => `${xAt(i)},${yCount(Number(r.count) || 0)}`).join(' ');

    return { width, height, pad, innerW, innerH, maxTtc, maxCount, xAt, yTtc, yCount, ttcPoints, countPoints };
  }, [rows]);

  if (!rows?.length) {
    return <p style={{ color: '#94a3b8', margin: 0 }}>{t('paymentDashboard.no_data')}</p>;
  }

  return (
    <div>
      <div className="payment-monthly-legend">
        <span>
          <i className="payment-legend-dot ttc" />
          {t('paymentDashboard.legend_ttc')}
        </span>
        <span>
          <i className="payment-legend-dot count" />
          {t('paymentDashboard.legend_count')}
        </span>
      </div>
      <div className="payment-monthly-chart">
        <svg viewBox={`0 0 ${chart.width} ${chart.height}`} preserveAspectRatio="none" role="img" aria-label={t('paymentDashboard.section_monthly')}>
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = chart.pad.top + chart.innerH * (1 - ratio);
            return (
              <line
                key={ratio}
                x1={chart.pad.left}
                y1={y}
                x2={chart.pad.left + chart.innerW}
                y2={y}
                stroke="#e2e8f0"
                strokeWidth="1"
              />
            );
          })}
          <polyline fill="none" stroke="#2563eb" strokeWidth="2.5" points={chart.ttcPoints} />
          <polyline fill="none" stroke="#10b981" strokeWidth="2" strokeDasharray="5 4" points={chart.countPoints} />
          {rows.map((row, i) => (
            <g key={row.key}>
              <circle cx={chart.xAt(i)} cy={chart.yTtc(Number(row.ttc) || 0)} r="3.5" fill="#2563eb" />
              <circle cx={chart.xAt(i)} cy={chart.yCount(Number(row.count) || 0)} r="3" fill="#10b981" />
              <text
                x={chart.xAt(i)}
                y={chart.height - 6}
                textAnchor="middle"
                fontSize="9"
                fill="#94a3b8"
              >
                {row.label}
              </text>
            </g>
          ))}
        </svg>
      </div>
      <div className="payment-monthly-scroll">
        <table className="payment-table">
          <thead>
            <tr>
              <th>{t('paymentDashboard.col_metric')}</th>
              {rows.map((row) => (
                <th key={row.key}>{row.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>{t('paymentDashboard.col_ttc')}</strong></td>
              {rows.map((row) => (
                <td key={`${row.key}-ttc`}>{fmt(row.ttc, currency)}</td>
              ))}
            </tr>
            <tr>
              <td><strong>{t('paymentDashboard.col_count')}</strong></td>
              {rows.map((row) => (
                <td key={`${row.key}-count`}>{row.count}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PodiumBlock({ title, items, currency }) {
  return (
    <div className="payment-card">
      <h2>{title}</h2>
      {!items?.length ? (
        <p style={{ color: '#94a3b8', margin: 0 }}>—</p>
      ) : (
        <ul className="podium-list">
          {items.map((item, i) => (
            <li key={item.examId}>
              <span className="podium-rank">{MEDALS[i] || `${item.rank}.`}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong>{item.examName}</strong>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  {item.count} vente{item.count > 1 ? 's' : ''}
                </div>
              </span>
              <span style={{ fontWeight: 600 }}>{fmt(item.ttc, currency)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function PaymentDashboardPage() {
  const { t, i18n } = useTranslation();
  const { isJagE, loading: authLoading } = useAuth();
  const locale = i18n.language;

  const [dashboard, setDashboard] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [dashError, setDashError] = useState('');

  const [purchases, setPurchases] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 1 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [listLoading, setListLoading] = useState(false);

  const currency = 'eur';

  const loadDashboard = useCallback(async () => {
    setDashLoading(true);
    setDashError('');
    try {
      const data = await fetchBillingAdminDashboard();
      if (data?.success) {
        setDashboard(data);
      } else {
        setDashError(t('paymentDashboard.load_error'));
      }
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === 'jag_e_required') {
        setDashError(t('paymentDashboard.forbidden'));
      } else {
        setDashError(err?.response?.data?.message || err.message || t('paymentDashboard.load_error'));
      }
    } finally {
      setDashLoading(false);
    }
  }, [t]);

  const loadPurchases = useCallback(
    async (page = 1) => {
      setListLoading(true);
      try {
        const data = await fetchBillingAdminPurchases({ page, limit: 15, search });
        if (data?.success) {
          setPurchases(data.items || []);
          setPagination(data.pagination || { page: 1, limit: 15, total: 0, totalPages: 1 });
        }
      } catch (err) {
        console.error('Purchases load failed:', err);
      } finally {
        setListLoading(false);
      }
    },
    [search]
  );

  useEffect(() => {
    if (!authLoading && isJagE) {
      loadDashboard();
    }
  }, [authLoading, isJagE, loadDashboard]);

  useEffect(() => {
    if (!authLoading && isJagE) {
      loadPurchases(1);
    }
  }, [authLoading, isJagE, search, loadPurchases]);

  if (authLoading) {
    return (
      <div className="payment-forbidden">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!isJagE) {
    return (
      <div className="payment-forbidden">
        <h2>{t('paymentDashboard.forbidden')}</h2>
        <p>{t('paymentDashboard.forbidden_hint')}</p>
      </div>
    );
  }

  if (dashLoading && !dashboard) {
    return (
      <div className="payment-forbidden">
        <LoadingSpinner size="large" />
        <p>{t('paymentDashboard.loading')}</p>
      </div>
    );
  }

  if (dashError && !dashboard) {
    return (
      <div className="payment-forbidden">
        <h2>{t('paymentDashboard.error_title')}</h2>
        <p>{dashError}</p>
        <button type="button" onClick={loadDashboard}>
          {t('paymentDashboard.retry')}
        </button>
      </div>
    );
  }

  const kpis = dashboard?.kpis || {};
  const periodTotals = dashboard?.periodTotals;
  const monthly = dashboard?.monthly || [];
  const quarterly = dashboard?.quarterly || [];
  const byProduct = dashboard?.byProduct || [];
  const podiums = dashboard?.podiums || {};
  const trends = dashboard?.trends || {};

  return (
    <div className="payment-dashboard">
      <div className="payment-dashboard-inner">
        <header className="payment-dashboard-header">
          <h1>{t('paymentDashboard.title')}</h1>
          <p>{t('paymentDashboard.subtitle', { days: kpis.accessDurationDays || 365 })}</p>
        </header>

        <h3 className="payment-section-title" style={{ marginTop: 20 }}>
          {t('paymentDashboard.section_periods')}
        </h3>
        <PeriodCards
          title={t('paymentDashboard.totals_ttc')}
          data={periodTotals}
          currency={currency}
          t={t}
        />

        <h3 className="payment-section-title">{t('paymentDashboard.section_monthly')}</h3>
        <div className="payment-card">
          <h2>{t('paymentDashboard.monthly_title')}</h2>
          <MonthlyTrendSection rows={monthly} currency={currency} t={t} />
        </div>

        <div className="payment-dashboard-grid cols-3" style={{ marginTop: 0 }}>
          <div className="payment-card payment-kpi">
            <span className="label">{t('paymentDashboard.kpi_active_buyers')}</span>
            <span className="value">{kpis.activeBuyers ?? 0}</span>
            <span className="sub">{t('paymentDashboard.kpi_active_buyers_hint')}</span>
          </div>
          <div className="payment-card payment-kpi">
            <span className="label">{t('paymentDashboard.kpi_paid')}</span>
            <span className="value">{kpis.paidCount ?? 0}</span>
          </div>
          <div className="payment-card payment-kpi">
            <span className="label">{t('paymentDashboard.kpi_buyers')}</span>
            <span className="value">{kpis.uniqueBuyers ?? 0}</span>
          </div>
          <div className="payment-card payment-kpi">
            <span className="label">{t('paymentDashboard.kpi_avg_basket')}</span>
            <span className="value">{fmt(kpis.avgBasketTtc, currency)}</span>
          </div>
          <div className="payment-card payment-kpi">
            <span className="label">{t('paymentDashboard.kpi_repeat')}</span>
            <span className="value">{kpis.repeatBuyers ?? 0}</span>
          </div>
          <div className="payment-card payment-kpi">
            <span className="label">{t('paymentDashboard.kpi_expiring')}</span>
            <span className="value">{kpis.expiringWithin30Days ?? 0}</span>
          </div>
          <div className="payment-card payment-kpi">
            <span className="label">{t('paymentDashboard.kpi_pending')}</span>
            <span className="value">{kpis.pendingPayments ?? 0}</span>
          </div>
        </div>

        <h3 className="payment-section-title">{t('paymentDashboard.section_quarterly')}</h3>
        <div className="payment-card">
          <div className="payment-table-wrap">
            <table className="payment-table">
              <thead>
                <tr>
                  <th>{t('paymentDashboard.col_quarter')}</th>
                  <th>{t('paymentDashboard.col_ht')}</th>
                  <th>{t('paymentDashboard.col_vat')}</th>
                  <th>{t('paymentDashboard.col_ttc')}</th>
                  <th>{t('paymentDashboard.col_count')}</th>
                </tr>
              </thead>
              <tbody>
                {quarterly.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ color: '#94a3b8' }}>
                      {t('paymentDashboard.no_data')}
                    </td>
                  </tr>
                ) : (
                  quarterly.map((row) => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      <td>{fmt(row.ht, currency)}</td>
                      <td>{fmt(row.tax, currency)}</td>
                      <td><strong>{fmt(row.ttc, currency)}</strong></td>
                      <td>{row.count}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <h3 className="payment-section-title">{t('paymentDashboard.section_by_product')}</h3>
        <div className="payment-card">
          <div className="payment-table-wrap">
            <table className="payment-table">
              <thead>
                <tr>
                  <th>{t('paymentDashboard.col_product')}</th>
                  <th>{t('paymentDashboard.col_count')}</th>
                  <th>{t('paymentDashboard.col_ht')}</th>
                  <th>{t('paymentDashboard.col_vat')}</th>
                  <th>{t('paymentDashboard.col_ttc')}</th>
                </tr>
              </thead>
              <tbody>
                {byProduct.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ color: '#94a3b8' }}>
                      {t('paymentDashboard.no_data')}
                    </td>
                  </tr>
                ) : (
                  byProduct.map((row) => (
                    <tr key={row.examId}>
                      <td>{row.examName}</td>
                      <td>{row.count}</td>
                      <td>{fmt(row.ht, currency)}</td>
                      <td>{fmt(row.tax, currency)}</td>
                      <td><strong>{fmt(row.ttc, currency)}</strong></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <h3 className="payment-section-title">{t('paymentDashboard.section_podium')}</h3>
        <div className="payment-dashboard-grid cols-3">
          <PodiumBlock
            title={t('paymentDashboard.podium_week')}
            items={podiums.week}
            currency={currency}
          />
          <PodiumBlock
            title={t('paymentDashboard.podium_month')}
            items={podiums.month}
            currency={currency}
          />
          <PodiumBlock
            title={t('paymentDashboard.podium_quarter')}
            items={podiums.quarter}
            currency={currency}
          />
        </div>

        <h3 className="payment-section-title">{t('paymentDashboard.section_trends')}</h3>
        <div className="payment-dashboard-grid cols-2">
          <div className="payment-card">
            <h2>{t('paymentDashboard.trend_daily')}</h2>
            <TrendChart rows={trends.dailyLast30} currency={currency} t={t} />
          </div>
          <div className="payment-card">
            <h2>{t('paymentDashboard.trend_weekly')}</h2>
            <TrendChart
              rows={trends.weeklyLast12}
              valueKey="ttc"
              labelKey="week"
              currency={currency}
              t={t}
            />
          </div>
        </div>

        <h3 className="payment-section-title">{t('paymentDashboard.section_channels')}</h3>
        <div className="payment-card">
          <div className="payment-table-wrap">
            <table className="payment-table">
              <thead>
                <tr>
                  <th>{t('paymentDashboard.col_channel')}</th>
                  <th>{t('paymentDashboard.col_count')}</th>
                  <th>{t('paymentDashboard.col_ttc')}</th>
                </tr>
              </thead>
              <tbody>
                {(kpis.bySource || []).length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ color: '#94a3b8' }}>
                      {t('paymentDashboard.no_data')}
                    </td>
                  </tr>
                ) : (
                  (kpis.bySource || []).map((row) => (
                    <tr key={row.source}>
                      <td>
                        <span className={`payment-badge ${row.source === 'invoice' ? 'invoice' : 'checkout'}`}>
                          {row.source === 'invoice'
                            ? t('paymentDashboard.channel_invoice')
                            : t('paymentDashboard.channel_checkout')}
                        </span>
                      </td>
                      <td>{row.count}</td>
                      <td>{fmt(row.ttc, currency)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <h3 className="payment-section-title">{t('paymentDashboard.section_buyers')}</h3>
        <div className="payment-card">
          <div className="payment-toolbar">
            <input
              type="search"
              placeholder={t('paymentDashboard.search_placeholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setSearch(searchInput.trim());
              }}
            />
            <button type="button" onClick={() => setSearch(searchInput.trim())}>
              {t('paymentDashboard.search')}
            </button>
            <button type="button" onClick={() => loadDashboard()} disabled={dashLoading}>
              {t('paymentDashboard.refresh')}
            </button>
          </div>

          {listLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="payment-table-wrap">
              <table className="payment-table">
                <thead>
                  <tr>
                    <th>{t('paymentDashboard.col_buyer')}</th>
                    <th>{t('paymentDashboard.col_certification')}</th>
                    <th>{t('paymentDashboard.col_purchased')}</th>
                    <th>{t('paymentDashboard.col_valid_until')}</th>
                    <th>{t('paymentDashboard.col_ttc')}</th>
                    <th>{t('paymentDashboard.col_channel')}</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ color: '#94a3b8' }}>
                        {t('paymentDashboard.no_data')}
                      </td>
                    </tr>
                  ) : (
                    purchases.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <div><strong>{row.fullName || row.username}</strong></div>
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{row.email}</div>
                        </td>
                        <td>{row.examName}</td>
                        <td>{fmtDate(row.purchasedAt, locale)}</td>
                        <td>{fmtDate(row.validUntil, locale)}</td>
                        <td>{fmt(row.ttc, row.currency)}</td>
                        <td>
                          <span className={`payment-badge ${row.source === 'invoice' ? 'invoice' : 'checkout'}`}>
                            {row.source === 'invoice'
                              ? t('paymentDashboard.channel_invoice')
                              : t('paymentDashboard.channel_checkout')}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="payment-pagination">
            <button
              type="button"
              disabled={pagination.page <= 1 || listLoading}
              onClick={() => loadPurchases(pagination.page - 1)}
            >
              ←
            </button>
            <span>
              {t('paymentDashboard.page_of', {
                page: pagination.page,
                total: pagination.totalPages,
              })}
            </span>
            <button
              type="button"
              disabled={pagination.page >= pagination.totalPages || listLoading}
              onClick={() => loadPurchases(pagination.page + 1)}
            >
              →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
